import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DocumentPreview from "@/components/application/DocumentPreview";

interface D4MedicalSectionProps {
  file: File | null;
  setFile: (file: File | null) => void;
  preview: string | null;
  setPreview: (preview: string | null) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

const D4MedicalSection: React.FC<D4MedicalSectionProps> = ({
  file,
  setFile,
  preview,
  setPreview,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      setFile(f);
      const fileUrl = URL.createObjectURL(f);
      setPreview(fileUrl);
    }
  };

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasReport = Boolean(file) || Boolean(preview);
    if (!hasReport) {
      toast({
        title: "Missing File",
        description: "Please upload your D4 medical report.",
        variant: "destructive",
      });
      return;
    }
    onSubmit();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>D4 Medical Report Upload</CardTitle>
        <CardDescription>Upload your latest D4 medical report (scan or PDF).</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="d4MedicalFile">D4 Medical Report</Label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => triggerFileInput("d4MedicalFile")}
            >
              {preview ? (
                <div className="flex flex-col items-center space-y-2">
                  <DocumentPreview
                    src={preview}
                    alt="D4 medical preview"
                    file={file}
                    imageClassName="max-h-32 object-contain"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreview(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              ) : (
                <>
                  <FileUp className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Click to upload D4 medical report</p>
                </>
              )}
            </div>
            <Input
              id="d4MedicalFile"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept="image/png,image/jpeg,application/pdf"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb hover:bg-amber-500 text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit D4 Medical"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default D4MedicalSection;
